/*
 * BRXL privacy runtime
 *
 * The runtime is loaded synchronously from theme.liquid, before
 * content_for_header. It establishes a safe UNKNOWN state first, then
 * resolves the Shopify/OneTrust region and releases only the vendors that are
 * allowed for the current consent state.
 *
 * This is the storefront layer. Shopify Customer Events, OneTrust, GTM, app
 * embeds, checkout extensions and server-side destinations still require the
 * corresponding admin-side configuration described in the implementation
 * checklist.
 */
(() => {
  'use strict';

  const script = document.currentScript;
  const config = {
    gtmId: script && script.dataset.gtmId,
    googleAdsId: script && script.dataset.googleAdsId,
    oneTrustDomainId: script && script.dataset.oneTrustDomainId,
    categoryAnalytics: (script && script.dataset.onetrustCategoryAnalytics) || 'C0002',
    categoryPreferences: (script && script.dataset.onetrustCategoryPreferences) || 'C0003',
    categoryMarketing: (script && script.dataset.onetrustCategoryMarketing) || 'C0004',
    categorySale: (script && script.dataset.onetrustCategorySale) || '',
    pageType: (script && script.dataset.pageType) || '',
    shopifyFirstPartyAnalyticsEnabled: !script || script.dataset.shopifyFirstPartyAnalyticsEnabled !== 'false',
    fireworkBusinessId: (script && script.dataset.fireworkBusinessId) || ''
  };

  const KEYS = {
    choice: 'brxl_privacy_explicit_choice',
    optOut: 'brxl_privacy_manual_opt_out',
    legacyChoice: 'brxl_ca_privacy_choice_confirmed',
    region: 'brxl_privacy_region'
  };

  const CATEGORY_COOKIE_NAMES = [
    /^_ga(?:$|_)/i,
    /^_gid$/i,
    /^_gcl_/i,
    /^_dc_gtm_/i,
    /^_fbp$/i,
    /^_fbc$/i,
    /^_uet/i,
    /^MUID$/i,
    /^__kla/i,
    /^kla_id$/i,
    /^fw_/i,
    /^firework/i,
    /^_?cartsee/i,
    /^cartx/i,
    /^impact/i,
    /^goaff/i,
    /^kaching/i,
    /^yotpo/i,
    /^stamped/i,
    /^trustpilot/i
  ];

  const CATEGORY_STORAGE_NAMES = [
    /^_ga/i,
    /^_gid$/i,
    /^_gcl_/i,
    /^_fbp/i,
    /^_fbc/i,
    /^_uet/i,
    /^muid$/i,
    /^__kla/i,
    /^kla_/i,
    /^firework/i,
    /^fw_/i,
    /^_?cartsee/i,
    /^cartx/i,
    /^impact/i,
    /^goaff/i,
    /^kaching/i,
    /^yotpo/i,
    /^stamped/i,
    /^trustpilot/i,
    /^pixel/i,
    /^tracking/i,
    /^telemetry/i,
    /^rum_/i
  ];

  const SENSITIVE_KEYS = new Set([
    'search_term',
    'search_terms',
    'search_query',
    'query_string',
    'full_query',
    'full_url',
    'search',
    'searchterm',
    'searchquery',
    'keyword',
    'keywords',
    'query',
    'q',
    'term',
    'checkout_token',
    'checkouttoken',
    'email',
    'email_address',
    'phone',
    'phone_number',
    'address',
    'first_name',
    'last_name',
    'customer_email',
    'customer_phone',
    'user_email',
    'user_phone'
  ]);

  const URL_KEYS = new Set([
    'page_location',
    'page_referrer',
    'document_location',
    'document_referrer',
    'url',
    'referrer',
    'source_url',
    'destination_url',
    'landing_page'
  ]);

  const VENDOR_POLICIES = {
    gtm: { categories: ['analytics', 'marketing'] },
    google_analytics: { categories: ['analytics'] },
    shopify_customer_events: { categories: ['analytics', 'marketing', 'preferences'] },
    google_ads: { categories: ['marketing'] },
    meta: { categories: ['marketing', 'sale'] },
    microsoft: { categories: ['marketing'] },
    // Firework's storefront component is a product-content feature. Keep the
    // SDK, feed configuration and media available in every consent state, but
    // gate its analytics/pixel endpoints and identifiers separately.
    firework_ui: { categories: ['functional'] },
    firework_tracking: { categories: ['marketing'] },
    cartsee_tracking: { categories: ['analytics'] },
    cartsee_ui: { categories: ['functional'] },
    impact: { categories: ['marketing', 'sale'] },
    goaffpro: { categories: ['marketing', 'sale'] },
    kaching: { categories: ['analytics'] },
    klaviyo_tracking: { categories: ['marketing'] },
    klaviyo_form: { categories: ['functional'] },
    yotpo_tracking: { categories: ['analytics', 'marketing'] },
    stamped_tracking: { categories: ['analytics', 'marketing'] },
    trustpilot_tracking: { categories: ['analytics', 'marketing'] },
    accessibility_tracking: { categories: ['analytics'] },
    simprosys: { categories: ['marketing'] },
    unknown_tracking: { categories: ['analytics', 'marketing'] }
  };

  // CartSee's launcher and form UI store campaign presentation state locally.
  // This list preserves the intended UI/tracking split so the temporary
  // full-runtime exception below can be removed without redesigning storage
  // classification.
  const CARTSEE_UI_STORAGE_NAMES = [
    /^_cartsee_(?:popup_config|popup_config_request_time|popup_lastchecktime_1|tempPop|stat|icon_winner_id|pop_show|state|show_from|step_|showCover_|sold_out_popup_config|cart_last_image|items|map|coupon(?:_store)?)$/i,
    /^cartsee_customer_locale$/i,
    /^cartx_company_id$/i
  ];

  // Temporary business exception: CartSee's launcher currently depends on the
  // same bootstrap, identifiers and request context as its tracking runtime.
  // Keep the complete integration available in every consent state until the
  // vendor provides a supported display-only mode and a runtime consent API.
  const CARTSEE_FUNCTIONAL_PRIORITY_OVERRIDE = true;

  function isCartseeVendor(vendor) {
    return vendor === 'cartsee_ui' || vendor === 'cartsee_tracking';
  }

  function isCartseeUiStorageName(name) {
    return CARTSEE_UI_STORAGE_NAMES.some((pattern) => pattern.test(String(name || '')));
  }

  const state = {
    region: 'UNKNOWN',
    regionReady: false,
    gpc: false,
    explicitChoice: 'none',
    analyticsAllowed: false,
    marketingAllowed: false,
    preferencesAllowed: false,
    saleOfDataAllowed: false,
    thirdPartyTrackingAllowed: false,
    shopifyFirstPartyAnalyticsAllowed: true,
    shopifyCustomerEventsAllowed: false,
    source: 'default',
    updatedAt: 0
  };

  const runtime = {
    oneTrustLoaded: false,
    oneTrustBound: false,
    oneTrustReadyAt: 0,
    customerPrivacyBound: false,
    customerPrivacyPoll: null,
    customerPrivacyLoadPromise: null,
    customerPrivacyLoadAttempts: 0,
    pendingShopifyConsent: null,
    shopifyConsentSyncing: false,
    regionPoll: null,
    domReady: false,
    gtmLoaded: false,
    googleAdsLoaded: false,
    klaviyoLoaded: false,
    fireworkLoadAttempted: false,
    fireworkLoaded: false,
    fireworkConsentSyncAttempts: 0,
    fireworkConsentSyncTimer: null,
    fireworkConsentSignature: '',
    lastFireworkAnalyticsConsent: null,
    lastFireworkCookieConsent: undefined,
    trackerScheduleStarted: false,
    preferenceRequested: false,
    preferenceLoop: false,
    manualOptOut: false,
    lastApplied: '',
    lastPublished: '',
    lastShopifyConsent: '',
    blockedResources: [],
    blockedResourceKeys: new Set(),
    releasedResourceKeys: new Set(),
    guardInstalled: false,
    dataLayerWrapped: false,
    searchEventSent: false,
    revocationReloadScheduled: false,
    nonessentialCleanupTimer: null,
    mutationObserver: null,
    nativeCookieSetter: null
  };

  function safeStorageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (error) {
      return;
    }
  }

  function safeStorageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (error) {
      return;
    }
  }

  function hasGpcSignal() {
    return navigator.globalPrivacyControl === true;
  }

  function isManualOptOut() {
    return runtime.manualOptOut || safeStorageGet(window.localStorage, KEYS.optOut) === '1';
  }

  function readStoredChoice() {
    const stored = safeStorageGet(window.localStorage, KEYS.choice);
    if (stored === 'accept' || stored === 'reject' || stored === 'custom') return stored;
    return 'none';
  }

  function writeStoredChoice(choice) {
    if (choice === 'none') {
      safeStorageRemove(window.localStorage, KEYS.choice);
      safeStorageRemove(window.localStorage, KEYS.legacyChoice);
      return;
    }
    safeStorageSet(window.localStorage, KEYS.choice, choice);
    safeStorageSet(window.localStorage, KEYS.legacyChoice, '1');
  }

  function normalizeRegion(value) {
    if (!value) return '';
    if (typeof value === 'object') {
      const country = String(value.country || value.countryCode || '').toUpperCase();
      const region = String(value.state || value.region || value.province || '').toUpperCase();
      if (country === 'US' && (region === 'CA' || region === 'CALIFORNIA')) return 'USCA';
      if (country) return country + (region ? '-' + region : '');
      return '';
    }
    const normalized = String(value).trim().toUpperCase().replace(/_/g, '-');
    if (normalized === 'USCA' || normalized === 'US-CA' || normalized === 'CA-US' || normalized === 'US:CA') return 'USCA';
    if (normalized === 'CA' || normalized === 'CALIFORNIA') return 'USCA';
    if (normalized === 'US') return 'US';
    return normalized;
  }

  function isCaliforniaRegion(region) {
    return normalizeRegion(region) === 'USCA';
  }

  function isDefinitiveOtherRegion(region) {
    const normalized = normalizeRegion(region);
    if (!normalized || normalized === 'US' || normalized === 'UNKNOWN') return false;
    return normalized !== 'USCA';
  }

  function getOneTrust() {
    return window.OneTrust || window.Optanon || null;
  }

  function getActiveGroups() {
    const groups = window.OnetrustActiveGroups || window.OptanonActiveGroups || '';
    return String(groups || '');
  }

  function parseConsentCookie() {
    const cookies = String(document.cookie || '').split(';');
    const item = cookies.find((cookie) => cookie.trim().indexOf('OptanonConsent=') === 0);
    if (!item) return null;
    try {
      const encoded = item.trim().slice('OptanonConsent='.length);
      const params = new URLSearchParams(decodeURIComponent(encoded));
      const groups = params.get('groups');
      if (!groups) return null;
      return groups.split(',').reduce((result, pair) => {
        const separator = pair.lastIndexOf(':');
        if (separator < 1) return result;
        result[pair.slice(0, separator)] = pair.slice(separator + 1) === '1';
        return result;
      }, {});
    } catch (error) {
      return null;
    }
  }

  function groupIsActive(groupId) {
    if (!groupId) return null;
    const groups = ',' + getActiveGroups().replace(/^,|,$/g, '') + ',';
    if (groups !== ',,' && groups.indexOf(',' + groupId + ',') !== -1) return true;
    const cookieGroups = parseConsentCookie();
    if (cookieGroups && typeof cookieGroups[groupId] === 'boolean') return cookieGroups[groupId];
    return null;
  }

  function findSaleCategoryId() {
    if (config.categorySale) return config.categorySale;
    const oneTrust = getOneTrust();
    try {
      if (!oneTrust || typeof oneTrust.GetDomainData !== 'function') return '';
      const data = oneTrust.GetDomainData();
      const groups = data && Array.isArray(data.Groups) ? data.Groups : [];
      const match = groups.find((group) => {
        const name = String(group && (group.GroupName || group.Name || group.groupName) || '').toLowerCase();
        return name.indexOf('sale') !== -1 || name.indexOf('share') !== -1 || name.indexOf('target') !== -1;
      });
      return match && (match.CustomGroupId || match.GroupId || match.groupId) || '';
    } catch (error) {
      return '';
    }
  }

  function isAlertBoxClosed() {
    const oneTrust = getOneTrust();
    try {
      return Boolean(oneTrust && typeof oneTrust.IsAlertBoxClosed === 'function' && oneTrust.IsAlertBoxClosed());
    } catch (error) {
      return false;
    }
  }

  function inferChoiceFromOneTrust() {
    const stored = readStoredChoice();
    if (!isAlertBoxClosed()) return stored;
    const cookieGroups = parseConsentCookie();
    if (!cookieGroups) return stored;
    const decisions = Object.values(cookieGroups);
    if (!decisions.length) return stored;
    const allOptionalOn = cookieGroups[config.categoryAnalytics] === true && cookieGroups[config.categoryMarketing] === true;
    const allOptionalOff = cookieGroups[config.categoryAnalytics] === false && cookieGroups[config.categoryMarketing] === false;
    if (allOptionalOn) return 'accept';
    if (allOptionalOff) return 'reject';
    return 'custom';
  }

  function readShopifyRegion() {
    try {
      const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.getRegion !== 'function') return '';
      return normalizeRegion(customerPrivacy.getRegion());
    } catch (error) {
      return '';
    }
  }

  function readOneTrustRegion() {
    const oneTrust = getOneTrust();
    try {
      if (!oneTrust || typeof oneTrust.getGeolocationData !== 'function') return '';
      return normalizeRegion(oneTrust.getGeolocationData());
    } catch (error) {
      return '';
    }
  }

  function getCategoryDecisions() {
    return {
      analytics: groupIsActive(config.categoryAnalytics),
      preferences: groupIsActive(config.categoryPreferences),
      marketing: groupIsActive(config.categoryMarketing),
      sale: groupIsActive(findSaleCategoryId())
    };
  }

  function getEffectiveState() {
    const region = state.region || 'UNKNOWN';
    const ca = isCaliforniaRegion(region);
    const regionReady = state.regionReady && (ca || isDefinitiveOtherRegion(region));
    const gpc = hasGpcSignal() || isManualOptOut();
    const oneTrustChoice = inferChoiceFromOneTrust();
    const explicitChoice = gpc && ca ? 'reject' : (state.explicitChoice !== 'none' ? state.explicitChoice : oneTrustChoice);
    const decisions = getCategoryDecisions();
    let analyticsAllowed = false;
    let marketingAllowed = false;
    let preferencesAllowed = false;
    let saleOfDataAllowed = false;

    if (regionReady && ca) {
      const choiceMade = explicitChoice !== 'none' && explicitChoice !== 'reject';
      analyticsAllowed = choiceMade && !gpc && decisions.analytics === true;
      marketingAllowed = choiceMade && !gpc && decisions.marketing === true;
      preferencesAllowed = choiceMade && !gpc && decisions.preferences === true;
      // If OneTrust has a dedicated sale/share category, its decision wins.
      // When it has not been mapped yet, targeting consent is the operational
      // proxy. GPC/manual opt-out always wins.
      saleOfDataAllowed = choiceMade && !gpc && decisions.marketing === true && decisions.sale !== false;
    } else if (regionReady) {
      analyticsAllowed = !gpc && decisions.analytics !== false;
      marketingAllowed = !gpc && decisions.marketing !== false;
      preferencesAllowed = !gpc && decisions.preferences !== false;
      saleOfDataAllowed = !gpc && decisions.sale !== false;
    }

    const thirdPartyTrackingAllowed = Boolean(
      regionReady &&
      !gpc &&
      (ca ? explicitChoice !== 'none' : true) &&
      (analyticsAllowed || marketingAllowed || preferencesAllowed)
    );

    return {
      region: ca ? 'USCA' : (regionReady ? 'OTHER' : 'UNKNOWN'),
      regionReady,
      gpc,
      explicitChoice: explicitChoice || 'none',
      analyticsAllowed,
      marketingAllowed,
      preferencesAllowed,
      saleOfDataAllowed,
      thirdPartyTrackingAllowed,
      // Shopify's native storefront analytics is the merchant's first-party
      // reporting path. Customer Events/Web Pixels remain on the third-party
      // consent gate below, so enabling native session reporting does not
      // release App Pixels before a CA visitor opts in.
      shopifyFirstPartyAnalyticsAllowed: Boolean(config.shopifyFirstPartyAnalyticsEnabled),
      shopifyCustomerEventsAllowed: thirdPartyTrackingAllowed,
      // Backward-compatible diagnostic alias. New code must use the two
      // explicit fields above.
      shopifyAnalyticsAllowed: Boolean(config.shopifyFirstPartyAnalyticsEnabled),
      source: gpc ? 'gpc-or-manual-opt-out' : (state.regionReady ? (state.source || 'region-signal') : 'default'),
      pageType: config.pageType,
      searchContextRedacted: ca || !regionReady,
      updatedAt: Date.now()
    };
  }

  function applyRegion(region, source) {
    const normalized = normalizeRegion(region);
    if (!normalized) return false;
    if (isCaliforniaRegion(normalized)) {
      state.region = 'USCA';
      state.regionReady = true;
    } else if (isDefinitiveOtherRegion(normalized)) {
      state.region = 'OTHER';
      state.regionReady = true;
    } else {
      return false;
    }
    state.source = source || 'region-signal';
    return true;
  }

  function getSafePageLocation() {
    try {
      return window.location.origin + window.location.pathname;
    } catch (error) {
      return '';
    }
  }

  function getSafeReferrer() {
    try {
      if (!document.referrer) return '';
      const referrer = new URL(document.referrer);
      return referrer.origin + referrer.pathname;
    } catch (error) {
      return '';
    }
  }

  function isSearchEvent(item) {
    return item && typeof item === 'object' && String(item.event || '') === 'search_performed';
  }

  function canPreserveAttribution(effective) {
    return effective.region === 'OTHER' && effective.regionReady && !effective.gpc &&
      (effective.analyticsAllowed || effective.marketingAllowed);
  }

  function sanitizeMeasurementUrl(value, preserveAttribution) {
    const parsed = new URL(value, window.location.origin);
    const query = new URLSearchParams();
    if (preserveAttribution) {
      // Preserve attribution, not arbitrary query strings/search terms/PII.
      const allowed = /^(utm_(source|medium|campaign|id|term|content|source_platform|creative_format|marketing_tactic)|gclid|dclid|gbraid|wbraid|fbclid|msclkid)$/i;
      parsed.searchParams.forEach((entry, key) => {
        if (allowed.test(key)) query.append(key, entry);
      });
    }
    const attribution = query.toString();
    return parsed.origin + parsed.pathname + (attribution ? '?' + attribution : '');
  }

  function cloneAndSanitize(value, options = {}, seen = new WeakSet()) {
    if (value === null || value === undefined) return value;
    if (typeof value !== 'object') return value;
    if (seen.has(value)) return undefined;
    seen.add(value);

    if (typeof value === 'function') return value;
    if (Object.prototype.toString.call(value) === '[object Arguments]') {
      // gtag commands must remain Arguments, including commands queued before
      // Google's SDK loads. Arrays are interpreted as a different protocol.
      return (function () { return arguments; }).apply(null,
        Array.prototype.slice.call(value).map((entry) => cloneAndSanitize(entry, options, seen)));
    }
    if (value instanceof Date) return value;
    if (value instanceof URL) return sanitizeMeasurementUrl(value.href, options.preserveAttribution);
    if (Array.isArray(value)) return value.map((entry) => cloneAndSanitize(entry, options, seen));

    const output = {};
    const allowRawSearch = options.allowRawSearch === true;
    Object.keys(value).forEach((key) => {
      const normalizedKey = key.toLowerCase();
      const rawValue = value[key];
      if (SENSITIVE_KEYS.has(normalizedKey)) {
        if (allowRawSearch && (normalizedKey === 'search_term' || normalizedKey === 'search_terms')) {
          output.search_term = String(rawValue || '').slice(0, 200);
        }
        return;
      }
      if (URL_KEYS.has(normalizedKey) || /(?:^|_)(?:url|uri|href|referrer)$/.test(normalizedKey)) {
        if (typeof rawValue === 'string' && rawValue) {
          try {
            output[key] = sanitizeMeasurementUrl(rawValue, options.preserveAttribution);
          } catch (error) {
            output[key] = getSafePageLocation();
          }
        } else {
          output[key] = rawValue;
        }
        return;
      }
      output[key] = cloneAndSanitize(rawValue, options, seen);
    });
    return output;
  }

  function sanitizeDataLayerItem(item) {
    const effective = getEffectiveState();
    const allowRawSearch = isSearchEvent(item) && effective.region === 'OTHER' && effective.analyticsAllowed;
    const sanitized = cloneAndSanitize(item, {
      allowRawSearch,
      preserveAttribution: canPreserveAttribution(effective)
    });
    if (sanitized && typeof sanitized === 'object' && sanitized.event === 'search_performed') {
      sanitized.brxl_search_destination = 'approved-analytics-owner';
      sanitized.page_location = getSafePageLocation();
      sanitized.page_referrer = getSafeReferrer();
    }
    return sanitized;
  }

  function ensureDataLayerGuard() {
    window.dataLayer = window.dataLayer || [];
    if (runtime.dataLayerWrapped || typeof window.dataLayer.push !== 'function') return;
    const dataLayer = window.dataLayer;
    const originalPush = dataLayer.push.bind(dataLayer);
    dataLayer.push = function guardedDataLayerPush(...items) {
      const sanitizedItems = items
        .map((item) => sanitizeDataLayerItem(item))
        .filter((item) => item !== undefined);
      if (!sanitizedItems.length) return dataLayer.length;
      return originalPush(...sanitizedItems);
    };
    runtime.dataLayerWrapped = true;
  }

  function getHost(url) {
    try {
      return new URL(url, window.location.href).hostname.toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function getUrlString(url) {
    if (!url) return '';
    if (typeof url === 'string') return url;
    try {
      return url.url || url.href || String(url);
    } catch (error) {
      return '';
    }
  }

  function identifyVendor(url) {
    const value = getUrlString(url).toLowerCase();
    const host = getHost(value);
    if (!value) return '';
    if (host === 'cdn.cookielaw.org' || host.endsWith('.cookielaw.org') || host === 'geolocation.onetrust.com') return 'onetrust';
    // Shopify's Web Pixels Manager can be served from the storefront origin.
    // Classify by path as well as host so Custom Pixel/App Pixel sandboxes do
    // not bypass the CA gate simply because they look first-party in DevTools.
    if (value.indexOf('/cdn/wpm/') !== -1 || value.indexOf('/web-pixels@') !== -1 || value.indexOf('/shopify/monorail/') !== -1) return 'shopify_customer_events';
    if (host === 'monorail-edge.shopifysvc.com' || host.endsWith('.shopifysvc.com')) return 'shopify_first_party_analytics';
    if (host === window.location.hostname.toLowerCase() && /\/api\/(?:event\/)?collect(?:\?|$)/i.test(value)) return 'shopify_first_party_analytics';
    if (host === 'd39va5bdt61idd.cloudfront.net' && /\/carttrack(?:\.js)?(?:[?#]|$)/i.test(value)) return 'cartsee_tracking';
    if (host === 'd39va5bdt61idd.cloudfront.net' && (/\/cartanimate(?:\.js)?(?:[?#]|$)/i.test(value) || /\/static\/(?:css|js)\//i.test(value))) return 'cartsee_ui';
    if (host === 'ctxcollect.com' || host.endsWith('.ctxcollect.com')) return 'cartsee_tracking';
    if (host.endsWith('shopify.com') || host.endsWith('shopifycdn.com') || host.endsWith('shopifycs.com') || host.endsWith('shopify.dev')) return 'shopify';
    if (host === 'googletagmanager.com' || host.endsWith('.googletagmanager.com')) return 'gtm';
    if (host === 'google-analytics.com' || host.endsWith('.google-analytics.com') || value.indexOf('/g/collect') !== -1) return 'google_analytics';
    if (host === 'googleadservices.com' || host.endsWith('.googleadservices.com') || host === 'googlesyndication.com' || host.endsWith('.googlesyndication.com') || host === 'doubleclick.net' || host.endsWith('.doubleclick.net') || value.indexOf('/pagead/') !== -1) return 'google_ads';
    if (host === 'facebook.com' || host.endsWith('.facebook.com') || host === 'facebook.net' || host.endsWith('.facebook.net') || (value.indexOf('/tr') !== -1 && host.indexOf('facebook') !== -1) || host === 'graph.facebook.com') return 'meta';
    if (host === 'bat.bing.com' || host.endsWith('.bing.com') || host === 'clarity.ms' || host.endsWith('.clarity.ms')) return 'microsoft';
    const fireworkHost = host === 'fireworkapi1.com' || host.endsWith('.fireworkapi1.com') ||
      host === 'fireworktv.com' || host.endsWith('.fireworktv.com') ||
      host === 'fireworkadservices1.com' || host.endsWith('.fireworkadservices1.com') ||
      host === 'asset.fwcdn3.com' || host.endsWith('.fwcdn3.com');
    if (host === 'p2.fwpixel.com' || host.endsWith('.fwpixel.com') ||
        host === 'fireworkanalytics.com' || host.endsWith('.fireworkanalytics.com') ||
        (fireworkHost && /\/trk(?:\/|\?|$)/i.test(value))) return 'firework_tracking';
    if (fireworkHost) return 'firework_ui';
    if (host.indexOf('cartsee') !== -1 || host.indexOf('cartx.cloud') !== -1 || host.endsWith('.clink-lists.com')) {
      if (/(?:cartxtrack|\/track(?:\/|\?|$)|\/collect(?:\/|\?|$)|\/analytics(?:\/|\?|$)|\/event(?:\/|\?|$)|\/monitor\/request(?:\?|$)|\/form\/out\/view(?:\?|$))/i.test(value)) return 'cartsee_tracking';
      return 'cartsee_ui';
    }
    if (host.indexOf('impact.com') !== -1 || host.indexOf('impactradius') !== -1) return 'impact';
    if (host.indexOf('goaffpro') !== -1) return 'goaffpro';
    if (host.indexOf('kaching') !== -1) return 'kaching';
    if ((host === 'static-tracking.klaviyo.com') || (host === 'a.klaviyo.com' && (value.indexOf('/onsite/track-analytics') !== -1 || value.indexOf('/forms/api/v3/geo-ip') !== -1)) || host.indexOf('trk.klaviyo.com') !== -1) return 'klaviyo_tracking';
    if (host === 'static.klaviyo.com' || host.endsWith('.klaviyo.com')) return 'klaviyo_form';
    if (host.indexOf('yotpo') !== -1) return 'yotpo_tracking';
    if (host.indexOf('stamped') !== -1) return 'stamped_tracking';
    if (host.indexOf('trustpilot') !== -1) return 'trustpilot_tracking';
    if (host.indexOf('accessibility') !== -1 || host.indexOf('accessib') !== -1 || host.indexOf('ada') !== -1) return 'accessibility_tracking';
    if (host.indexOf('simprosys') !== -1 || host.indexOf('google-shopping') !== -1) return 'simprosys';
    if (host === window.location.hostname.toLowerCase() && /\/api\/collect(?:\?|$)/i.test(value)) return 'accessibility_tracking';
    const trackingWord = /(pixel|track|analytics|telemetry|remarket|conversion|collect|beacon|ads?\b|tagmanager|gtm)/i;
    if (trackingWord.test(value) && host && host !== window.location.hostname.toLowerCase()) return 'unknown_tracking';
    return '';
  }

  function categoryAllowed(category, effective) {
    if (category === 'functional' || category === 'onetrust' || category === 'shopify') return true;
    if (!effective.regionReady || effective.gpc) return false;
    if (category === 'analytics') return effective.analyticsAllowed;
    if (category === 'marketing') return effective.marketingAllowed;
    if (category === 'sale') return effective.saleOfDataAllowed;
    if (category === 'preferences') return effective.preferencesAllowed;
    return false;
  }

  function isVendorAllowed(vendor, effective = getEffectiveState()) {
    if (!vendor) return effective.region === 'OTHER' && effective.regionReady && !effective.gpc;
    if (vendor === 'onetrust' || vendor === 'shopify') return true;
    if (CARTSEE_FUNCTIONAL_PRIORITY_OVERRIDE && isCartseeVendor(vendor)) return true;
    if (vendor === 'shopify_first_party_analytics') return effective.shopifyFirstPartyAnalyticsAllowed === true;
    if (vendor === 'shopify_customer_events') return effective.shopifyCustomerEventsAllowed === true;
    const policy = VENDOR_POLICIES[vendor] || VENDOR_POLICIES.unknown_tracking;
    if (vendor === 'klaviyo_form' || vendor === 'firework_ui') return true;
    if (!effective.regionReady || effective.gpc) return false;
    if (effective.region === 'USCA' && effective.explicitChoice === 'none') return false;
    return policy.categories.some((category) => categoryAllowed(category, effective));
  }

  function vendorForResource(resource) {
    return identifyVendor(getUrlString(resource));
  }

  function shouldBlockResource(resource) {
    const vendor = vendorForResource(resource);
    const value = getUrlString(resource).toLowerCase();
    const effective = getEffectiveState();
    if (!vendor) return false;
    // Keep review/accessibility/bundle UI usable while gating their
    // session/event/invite/telemetry endpoints until a decision exists.
    if (vendor === 'accessibility_tracking' || vendor === 'yotpo_tracking' || vendor === 'stamped_tracking' || vendor === 'trustpilot_tracking' || vendor === 'kaching') {
      const telemetryEndpoint = /(track|event|analytics|telemetry|session|invite|collect|pixel|beacon|conversion)/i.test(value);
      if (!telemetryEndpoint) return false;
      return !(effective.analyticsAllowed || effective.marketingAllowed) || effective.gpc || (effective.region === 'USCA' && effective.explicitChoice === 'none');
    }
    return !isVendorAllowed(vendor);
  }

  function requestBodyText(body) {
    if (body === null || body === undefined) return '';
    if (typeof body === 'string') return body;
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body.toString();
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      try {
        return Array.from(body.entries()).map(([key, value]) => key + '=' + String(value)).join('&');
      } catch (error) {
        return '';
      }
    }
    if (typeof body === 'object') {
      try {
        return JSON.stringify(body);
      } catch (error) {
        return '';
      }
    }
    return String(body);
  }

  function sanitizeVendorRequestBody(url, body) {
    const value = getUrlString(url).toLowerCase();
    const vendor = vendorForResource(url);
    if (CARTSEE_FUNCTIONAL_PRIORITY_OVERRIDE && isCartseeVendor(vendor)) return body;
    if (vendor !== 'cartsee_ui' || !/\/cartsee\/form\/out\/load(?:\?|$)/i.test(value) || typeof body !== 'string') return body;
    try {
      const payload = JSON.parse(body);
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return body;
      payload.cid = null;
      payload.formUid = null;
      payload.shopifyUserId = null;
      payload.parameters = '';
      payload.abMapping = null;
      return JSON.stringify(payload);
    } catch (error) {
      return body;
    }
  }

  function bodyContainsSensitiveContext(body) {
    const text = requestBodyText(body);
    if (!text) return false;
    let decoded = text;
    try {
      decoded = decodeURIComponent(text.replace(/\+/g, ' '));
    } catch (error) {
      decoded = text;
    }
    const normalized = decoded.toLowerCase();
    if (/["']?(?:search[_-]?(?:term|query)|query[_-]?string|full[_-]?(?:url|query)|checkout[_-]?token|customer[_-]?(?:email|phone)|email[_-]?(?:address)?|phone[_-]?number|first[_-]?name|last[_-]?name|referrer|page[_-]?(?:location|url))["']?\s*[:=]/i.test(normalized)) return true;
    // A beacon often serializes the document URL as `dl=`/`url=` instead of
    // JSON. Treat a query-bearing value as sensitive even when the key name is
    // not present in the decoded payload.
    if (/(?:^|[?&])(?:q|query|term|search|keyword)=/i.test(normalized)) return true;
    const searchElement = document.querySelector('[data-brxl-search-page]');
    const currentTerm = searchElement && searchElement.getAttribute('data-brxl-search-term');
    return Boolean(currentTerm && currentTerm.length > 2 && normalized.indexOf(String(currentTerm).toLowerCase()) !== -1);
  }

  function bodyContainsRawSearchValue(body) {
    const text = requestBodyText(body);
    if (!text) return false;
    let decoded = text;
    try {
      decoded = decodeURIComponent(text.replace(/\+/g, ' '));
    } catch (error) {
      decoded = text;
    }
    const normalized = decoded.toLowerCase();
    if (/(?:search[_-]?(?:term|query)|query[_-]?string|full[_-]?query|keyword|\bq\b|\bterm\b)["']?\s*[:=]/i.test(normalized)) return true;
    if (/(?:^|[?&])(?:q|query|term|search|keyword)=/i.test(normalized)) return true;
    const searchElement = document.querySelector('[data-brxl-search-page]');
    const currentTerm = searchElement && searchElement.getAttribute('data-brxl-search-term');
    return Boolean(currentTerm && currentTerm.length > 2 && normalized.indexOf(String(currentTerm).toLowerCase()) !== -1);
  }

  function bodyContainsRestrictedIdentityContext(body) {
    const text = requestBodyText(body);
    if (!text) return false;
    let decoded = text;
    try {
      decoded = decodeURIComponent(text.replace(/\+/g, ' '));
    } catch (error) {
      decoded = text;
    }
    return /["']?(?:checkout[_-]?token|customer[_-]?(?:email|phone)|email(?:[_-]?address)?|phone(?:[_-]?number)?|first[_-]?name|last[_-]?name|address)["']?\s*[:=]/i.test(decoded);
  }

  function isApprovedSensitiveRequest(url, vendor, body) {
    const value = getUrlString(url).toLowerCase();
    const effective = getEffectiveState();
    if (CARTSEE_FUNCTIONAL_PRIORITY_OVERRIDE && isCartseeVendor(vendor)) return true;
    if (vendor === 'shopify_first_party_analytics') {
      // Native Shopify reporting may retain a session, product and cart
      // context. Raw search terms, checkout tokens and direct identifiers are
      // still rejected by the outer request guard.
      return !bodyContainsRawSearchValue(body) && !bodyContainsRestrictedIdentityContext(body);
    }
    if (vendor === 'shopify_customer_events') {
      return isVendorAllowed(vendor, effective) && !bodyContainsRawSearchValue(body) && !bodyContainsRestrictedIdentityContext(body);
    }
    if (vendor === 'google_analytics' || vendor === 'gtm') {
      if (!(effective.region === 'OTHER' && effective.regionReady && effective.analyticsAllowed && !effective.gpc)) return false;
      // Raw search values are reserved for the dedicated `search_performed`
      // event. A generic page_view beacon must not carry q= or the visitor's
      // search term, even when the visitor is outside CA.
      if (bodyContainsRawSearchValue(body)) {
        let decoded = requestBodyText(body);
        try {
          decoded = decodeURIComponent(decoded.replace(/\+/g, ' '));
        } catch (error) {
          // Keep the raw body if it is not URI encoded.
        }
        return /search_performed/i.test(decoded);
      }
      return true;
    }
    // A Klaviyo subscription request is an explicit user action. It may carry
    // the email/phone the visitor typed, while Klaviyo behavioral telemetry
    // remains blocked by the separate tracking vendor gate.
    if (vendor === 'klaviyo_form' && /\/forms?\/|\/subscribe|\/subscriptions?/i.test(value)) return true;
    // Dormant fallback for the future split mode: an explicit CartSee signup
    // may send the address/phone entered by the visitor, while attribution
    // telemetry returns to the tracking gate.
    if (vendor === 'cartsee_ui' && /\/(?:subscribe|signup)(?:\/|\?|$)/i.test(value)) {
      return !bodyContainsRawSearchValue(body) && !/["']?(?:checkout[_-]?token|address|first[_-]?name|last[_-]?name)["']?\s*[:=]/i.test(requestBodyText(body));
    }
    return false;
  }

  function shouldBlockRequest(url, body) {
    if (shouldBlockResource(url)) return true;
    const vendor = vendorForResource(url);
    // Preserve first-party commerce, account and checkout requests. The body
    // guard is for third-party destinations and known telemetry endpoints;
    // blocking a Shopify form submission because it contains an email would
    // break a core user action without reducing third-party disclosure.
    if (!vendor || vendor === 'shopify' || vendor === 'onetrust') return false;
    const requestContext = body || url;
    if (!bodyContainsSensitiveContext(body) && !bodyContainsSensitiveContext(url)) return false;
    return !isApprovedSensitiveRequest(url, vendor, requestContext);
  }

  function resourceKey(resource) {
    const value = getUrlString(resource);
    return value ? value.split('#')[0] : '';
  }

  function queueBlockedResource(node, originalUrl, vendor) {
    const key = resourceKey(originalUrl);
    if (!key || runtime.releasedResourceKeys.has(key) || runtime.blockedResourceKeys.has(key)) return;
    runtime.blockedResourceKeys.add(key);
    runtime.blockedResources.push({ node, originalUrl: key, vendor, inlineText: '' });
  }

  function inlineScriptVendor(node) {
    if (!node || !node.textContent) return '';
    const source = String(node.textContent).toLowerCase();
    if (/\b(wpmloader|webpixelsmanager|web-pixels@|monorailendpoint)\b/.test(source)) return 'shopify_customer_events';
    if (/\b(fbq|fbevents|connect\.facebook|facebook\.com\/tr)\b/.test(source)) return 'meta';
    if (/\b(googletagmanager|google-analytics|gtag\s*\(|google_conversion|doubleclick)\b/.test(source)) return 'google_analytics';
    if (/\b(_uetq|bat\.bing|clarity)\b/.test(source)) return 'microsoft';
    if (/\b(klaviyo|_learnq|onsite\/track-analytics)\b/.test(source)) return 'klaviyo_tracking';
    if (/\b(fwcdn3|fwpixel|firework)\b/.test(source)) return 'firework_ui';
    if (/\b(cartanimate|cartsee-form-c|cartx\.cloud|clink-lists)\b/.test(source)) return 'cartsee_ui';
    if (/\b(carttrack|cartxtrack)\b/.test(source)) return 'cartsee_tracking';
    if (/\b(impactradius|goaffpro|kaching)\b/.test(source)) return 'unknown_tracking';
    return '';
  }

  function stripBlockedScript(node, originalUrl) {
    if (!node || !node.setAttribute) return;
    node.setAttribute('data-brxl-blocked', 'true');
    node.setAttribute('data-brxl-original-src', originalUrl);
    try {
      node.removeAttribute('src');
      node.removeAttribute('data-src');
      node.removeAttribute('data-plugin-src');
    } catch (error) {
      return;
    }
  }

  function inspectScriptNode(node) {
    if (!node || node.nodeType !== 1 || String(node.tagName).toUpperCase() !== 'SCRIPT') return false;
    const originalUrl = node.getAttribute('data-brxl-original-src') || node.getAttribute('src') || node.getAttribute('data-src') || node.getAttribute('data-plugin-src');
    if (node.hasAttribute('data-brxl-allow')) return false;
    if (originalUrl) {
      if (!shouldBlockResource(originalUrl)) return false;
      const vendor = vendorForResource(originalUrl);
      queueBlockedResource(node, originalUrl, vendor);
      stripBlockedScript(node, originalUrl);
      return true;
    }
    const vendor = inlineScriptVendor(node);
    if (!vendor || isVendorAllowed(vendor)) return false;
    const key = 'inline:' + String(node.textContent || '').slice(0, 160);
    if (runtime.blockedResourceKeys.has(key)) return true;
    runtime.blockedResourceKeys.add(key);
    runtime.blockedResources.push({ node, originalUrl: key, vendor, inlineText: String(node.textContent || '') });
    node.setAttribute('data-brxl-blocked', 'true');
    node.textContent = '';
    return true;
  }

  function installCookieGuard() {
    try {
      const descriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');
      if (!descriptor || typeof descriptor.set !== 'function' || descriptor.set.__brxlGuarded) return;
      const originalSet = descriptor.set;
      runtime.nativeCookieSetter = originalSet;
      const guardedSet = function guardedCookieSet(value) {
        const name = String(value || '').split('=', 1)[0].trim();
        const effective = getEffectiveState();
        const blocked = CATEGORY_COOKIE_NAMES.some((pattern) => pattern.test(name));
        if (blocked && !isVendorAllowed(vendorForCookieName(name), effective)) return;
        return originalSet.call(this, value);
      };
      guardedSet.__brxlGuarded = true;
      Object.defineProperty(Document.prototype, 'cookie', {
        configurable: descriptor.configurable,
        enumerable: descriptor.enumerable,
        get: descriptor.get,
        set: guardedSet
      });
    } catch (error) {
      return;
    }
  }

  function installStorageGuard() {
    try {
      const originalSetItem = Storage.prototype.setItem;
      if (originalSetItem.__brxlGuarded) return;
      const guardedSetItem = function guardedStorageSetItem(key, value) {
        const name = String(key || '');
        const blocked = CATEGORY_STORAGE_NAMES.some((pattern) => pattern.test(name));
        const effective = getEffectiveState();
        if (blocked) {
          const vendor = vendorForStorageName(name);
          const allowed = vendor ? isVendorAllowed(vendor, effective) : Boolean(effective.analyticsAllowed || effective.marketingAllowed);
          if (!allowed) return;
        }
        return originalSetItem.call(this, key, value);
      };
      guardedSetItem.__brxlGuarded = true;
      Storage.prototype.setItem = guardedSetItem;
    } catch (error) {
      return;
    }
  }

  function installNetworkGuards() {
    if (runtime.guardInstalled) return;
    runtime.guardInstalled = true;

    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function guardedAppendChild(node) {
      if (inspectScriptNode(node)) return node;
      return originalAppendChild.call(this, node);
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function guardedInsertBefore(node, reference) {
      if (inspectScriptNode(node)) return node;
      return originalInsertBefore.call(this, node, reference);
    };

    const originalSetAttribute = Element.prototype.setAttribute;
    Element.prototype.setAttribute = function guardedSetAttribute(name, value) {
      const attr = String(name || '').toLowerCase();
      const tagName = String(this.tagName).toUpperCase();
      if (tagName === 'SCRIPT' && (attr === 'src' || attr === 'data-src' || attr === 'data-plugin-src')) {
        const url = String(value || '');
        if (shouldBlockResource(url)) {
          queueBlockedResource(this, url, vendorForResource(url));
          return originalSetAttribute.call(this, 'data-brxl-original-src', url);
        }
      }
      if ((tagName === 'IMG' && attr === 'src') || (tagName === 'IFRAME' && attr === 'src') || (tagName === 'LINK' && attr === 'href')) {
        const url = String(value || '');
        if (shouldBlockRequest(url)) {
          return originalSetAttribute.call(this, 'data-brxl-blocked-resource', url);
        }
      }
      return originalSetAttribute.call(this, name, value);
    };

    try {
      const scriptSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
      if (scriptSrcDescriptor && scriptSrcDescriptor.set && !scriptSrcDescriptor.set.__brxlGuarded) {
        const originalSrcSet = scriptSrcDescriptor.set;
        const guardedSrcSet = function guardedScriptSrcSet(value) {
          const url = String(value || '');
          if (shouldBlockResource(url)) {
            queueBlockedResource(this, url, vendorForResource(url));
            return originalSetAttribute.call(this, 'data-brxl-original-src', url);
          }
          return originalSrcSet.call(this, value);
        };
        guardedSrcSet.__brxlGuarded = true;
        Object.defineProperty(HTMLScriptElement.prototype, 'src', {
          configurable: scriptSrcDescriptor.configurable,
          enumerable: scriptSrcDescriptor.enumerable,
          get: scriptSrcDescriptor.get,
          set: guardedSrcSet
        });
      }
    } catch (error) {
      // Older browsers may not expose the src descriptor.
    }

    // Google/Ads and several app pixels use an image beacon instead of
    // fetch/sendBeacon. Guard the property setter so an allowed CA session
    // cannot send a search URL through `<img src="...q=...">`.
    [
      typeof HTMLImageElement !== 'undefined' && [HTMLImageElement.prototype, 'src'],
      typeof HTMLIFrameElement !== 'undefined' && [HTMLIFrameElement.prototype, 'src'],
      typeof HTMLLinkElement !== 'undefined' && [HTMLLinkElement.prototype, 'href']
    ].filter(Boolean).forEach(([prototype, property]) => {
      try {
        const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
        if (!descriptor || typeof descriptor.set !== 'function' || descriptor.set.__brxlGuarded) return;
        const originalSet = descriptor.set;
        const guardedSet = function guardedResourceSet(value) {
          const url = String(value || '');
          if (shouldBlockRequest(url)) {
            originalSetAttribute.call(this, 'data-brxl-blocked-resource', url);
            return;
          }
          return originalSet.call(this, value);
        };
        guardedSet.__brxlGuarded = true;
        Object.defineProperty(prototype, property, {
          configurable: descriptor.configurable,
          enumerable: descriptor.enumerable,
          get: descriptor.get,
          set: guardedSet
        });
      } catch (error) {
        // Some browser contexts expose non-configurable resource properties.
      }
    });

    const originalFetch = window.fetch;
    if (typeof originalFetch === 'function' && !originalFetch.__brxlGuarded) {
      const guardedFetch = function guardedFetch(input, init) {
        const url = getUrlString(input);
        const body = sanitizeVendorRequestBody(url, init && init.body);
        if (shouldBlockRequest(url, body)) {
          if (typeof Response === 'function') return Promise.resolve(new Response(null, { status: 204 }));
          return Promise.resolve({ ok: true, status: 204, text: () => Promise.resolve(''), json: () => Promise.resolve({}) });
        }
        const nextInit = init && body !== init.body ? { ...init, body } : init;
        return originalFetch.call(this, input, nextInit);
      };
      guardedFetch.__brxlGuarded = true;
      window.fetch = guardedFetch;
    }

    if (navigator.sendBeacon && !navigator.sendBeacon.__brxlGuarded) {
      const originalBeacon = navigator.sendBeacon.bind(navigator);
      const guardedBeacon = function guardedBeacon(url, data) {
        const body = sanitizeVendorRequestBody(url, data);
        if (shouldBlockRequest(url, body)) return true;
        return originalBeacon(url, body);
      };
      guardedBeacon.__brxlGuarded = true;
      navigator.sendBeacon = guardedBeacon;
    }

    try {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
      if (!originalOpen.__brxlGuarded) {
        XMLHttpRequest.prototype.open = function guardedOpen(method, url, ...args) {
          this.__brxlRequestUrl = getUrlString(url);
          this.__brxlBlocked = shouldBlockRequest(this.__brxlRequestUrl);
          return originalOpen.call(this, method, url, ...args);
        };
        XMLHttpRequest.prototype.send = function guardedSend(body) {
          const requestBody = sanitizeVendorRequestBody(this.__brxlRequestUrl, body);
          if (this.__brxlBlocked || shouldBlockRequest(this.__brxlRequestUrl, requestBody)) {
            window.setTimeout(() => {
              try {
                this.dispatchEvent(new Event('load'));
                this.dispatchEvent(new Event('loadend'));
              } catch (error) {
                return;
              }
            }, 0);
            return undefined;
          }
          return originalSend.call(this, requestBody);
        };
        XMLHttpRequest.prototype.open.__brxlGuarded = true;
      }
    } catch (error) {
      // XHR is unavailable in a few embedded contexts.
    }

    // A few Shopify app embeds use document.write to insert their loader. The
    // parser executes those tags before a MutationObserver callback can run,
    // so rewrite the markup first and keep blocked scripts inert.
    try {
      const originalWrite = Document.prototype.write;
      const originalWriteln = Document.prototype.writeln;
      const guardWrite = function guardedDocumentWrite(...parts) {
        const markup = parts.map((part) => String(part || '')).join('');
        if (!/<script\b/i.test(markup)) return originalWrite.apply(this, parts);
        const template = document.createElement('template');
        template.innerHTML = markup;
        template.content.querySelectorAll('script').forEach(inspectScriptNode);
        return originalWrite.call(this, template.innerHTML);
      };
      const guardWriteln = function guardedDocumentWriteln(...parts) {
        const markup = parts.map((part) => String(part || '')).join('');
        if (!/<script\b/i.test(markup)) return originalWriteln.apply(this, parts);
        const template = document.createElement('template');
        template.innerHTML = markup;
        template.content.querySelectorAll('script').forEach(inspectScriptNode);
        return originalWriteln.call(this, template.innerHTML);
      };
      guardWrite.__brxlGuarded = true;
      guardWriteln.__brxlGuarded = true;
      Document.prototype.write = guardWrite;
      Document.prototype.writeln = guardWriteln;
    } catch (error) {
      // document.write is unavailable in a few embedded contexts.
    }

    installCookieGuard();
    installStorageGuard();

    if (typeof MutationObserver === 'function') {
      const observer = new MutationObserver((records) => {
        records.forEach((record) => {
          Array.from(record.addedNodes || []).forEach((node) => {
            if (node.nodeType !== 1) return;
            if (String(node.tagName).toUpperCase() === 'SCRIPT') inspectScriptNode(node);
            if (node.querySelectorAll) node.querySelectorAll('script').forEach(inspectScriptNode);
          });
        });
      });
      observer.observe(document, { childList: true, subtree: true });
      runtime.mutationObserver = observer;
    }
  }

  function vendorForCookieName(name) {
    const value = String(name || '').toLowerCase();
    if (value.indexOf('_shopify_') === 0) return 'shopify_first_party_analytics';
    if (value.indexOf('_gcl_') === 0) return 'google_ads';
    if (value.indexOf('_ga') === 0 || value === '_gid' || value.indexOf('_dc_gtm_') === 0) return 'google_analytics';
    if (value === '_fbp' || value === '_fbc') return 'meta';
    if (value.indexOf('_uet') === 0 || value === 'muid') return 'microsoft';
    if (value.indexOf('__kla') === 0 || value === 'kla_id') return 'klaviyo_tracking';
    if (value.indexOf('fw_') === 0 || value.indexOf('firework') === 0) return 'firework_tracking';
    if (value.indexOf('cartsee') === 0 || value.indexOf('_cartsee') === 0 || value.indexOf('cartx') === 0) return 'cartsee_tracking';
    if (value.indexOf('impact') === 0) return 'impact';
    if (value.indexOf('goaff') === 0) return 'goaffpro';
    if (value.indexOf('kaching') === 0) return 'kaching';
    if (value.indexOf('yotpo') === 0) return 'yotpo_tracking';
    if (value.indexOf('stamped') === 0) return 'stamped_tracking';
    if (value.indexOf('trustpilot') === 0) return 'trustpilot_tracking';
    return '';
  }

  function vendorForStorageName(name) {
    const value = String(name || '').toLowerCase();
    if (isCartseeUiStorageName(value)) return 'cartsee_ui';
    if (value.indexOf('_shopify_') === 0) return 'shopify_first_party_analytics';
    if (value.indexOf('_gcl_') === 0) return 'google_ads';
    if (value.indexOf('_ga') === 0 || value === '_gid' || value.indexOf('_dc_gtm_') === 0) return 'google_analytics';
    if (value.indexOf('_fbp') === 0 || value.indexOf('_fbc') === 0) return 'meta';
    if (value.indexOf('_uet') === 0 || value === 'muid') return 'microsoft';
    if (value.indexOf('__kla') === 0 || value.indexOf('kla_') === 0) return 'klaviyo_tracking';
    if (value.indexOf('fw_') === 0 || value.indexOf('firework') === 0) return 'firework_tracking';
    if (value.indexOf('cartsee') === 0 || value.indexOf('_cartsee') === 0 || value.indexOf('cartx') === 0) return 'cartsee_tracking';
    if (value.indexOf('impact') === 0) return 'impact';
    if (value.indexOf('goaff') === 0) return 'goaffpro';
    if (value.indexOf('kaching') === 0) return 'kaching';
    if (value.indexOf('yotpo') === 0) return 'yotpo_tracking';
    if (value.indexOf('stamped') === 0) return 'stamped_tracking';
    if (value.indexOf('trustpilot') === 0) return 'trustpilot_tracking';
    return '';
  }

  function clearCookie(name) {
    const hostname = window.location.hostname.replace(/^www\./i, '');
    const values = [
      name + '=; Max-Age=0; path=/',
      name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/',
      name + '=; Max-Age=0; path=/; domain=.' + hostname,
      name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.' + hostname
    ];
    values.forEach((value) => {
      try {
        if (runtime.nativeCookieSetter) runtime.nativeCookieSetter.call(document, value);
        else document.cookie = value;
      } catch (error) {
        return;
      }
    });
  }

  function clearNonessentialIdentifiers() {
    const effective = getEffectiveState();
    String(document.cookie || '').split(';').forEach((entry) => {
      const name = entry.split('=')[0].trim();
      if (!name || !CATEGORY_COOKIE_NAMES.some((pattern) => pattern.test(name))) return;
      const vendor = vendorForCookieName(name);
      if (CARTSEE_FUNCTIONAL_PRIORITY_OVERRIDE && isCartseeVendor(vendor)) return;
      clearCookie(name);
    });
    [window.localStorage, window.sessionStorage].forEach((storage) => {
      try {
        Array.from({ length: storage.length }, (_, index) => storage.key(index)).forEach((key) => {
          if (!key || !CATEGORY_STORAGE_NAMES.some((pattern) => pattern.test(key))) return;
          const vendor = vendorForStorageName(key);
          if (!vendor || !isVendorAllowed(vendor, effective)) storage.removeItem(key);
        });
      } catch (error) {
        return;
      }
    });
    try {
      window.__kla_off = true;
    } catch (error) {
      return;
    }
  }

  function setKlaviyoTrackingSwitch(allowed) {
    try {
      window.__kla_off = !allowed;
      window.__klaviyoConsent = allowed ? 'granted' : 'denied';
    } catch (error) {
      return;
    }
    document.dispatchEvent(new CustomEvent('brxl:klaviyo-consent', { detail: { allowed } }));
  }

  function releaseBlockedResources() {
    const effective = getEffectiveState();
    const pending = runtime.blockedResources.splice(0);
    pending.forEach(({ node, originalUrl, vendor, inlineText }) => {
      runtime.blockedResourceKeys.delete(originalUrl);
      if (!isVendorAllowed(vendor, effective) || runtime.releasedResourceKeys.has(originalUrl)) {
        if (!isVendorAllowed(vendor, effective)) {
          runtime.blockedResources.push({ node, originalUrl, vendor, inlineText });
          runtime.blockedResourceKeys.add(originalUrl);
        }
        return;
      }
      runtime.releasedResourceKeys.add(originalUrl);
      const replacement = document.createElement('script');
      Array.from(node.attributes || []).forEach((attribute) => {
        if (attribute.name === 'data-brxl-blocked' || attribute.name === 'data-brxl-original-src' || attribute.name === 'src' || attribute.name === 'data-src' || attribute.name === 'data-plugin-src') return;
        if (inlineText && attribute.name === 'type' && attribute.value === 'text/plain') return;
        replacement.setAttribute(attribute.name, attribute.value);
      });
      replacement.setAttribute('data-brxl-allow', 'true');
      if (inlineText) {
        replacement.type = 'text/javascript';
        replacement.textContent = inlineText;
      }
      else replacement.src = originalUrl;
      replacement.setAttribute('data-brxl-released', 'true');
      (document.head || document.documentElement).appendChild(replacement);
    });
  }

  function loadScriptOnce(url, attributes = {}, onload) {
    if (shouldBlockResource(url)) {
      queueBlockedResource(document.createElement('script'), url, vendorForResource(url));
      return null;
    }
    const existing = Array.from(document.scripts || []).find((candidate) => candidate.src === url || candidate.getAttribute('src') === url);
    if (existing) {
      if (typeof onload === 'function') {
        if (existing.dataset.brxlLoaded === 'true') onload();
        else existing.addEventListener('load', onload, { once: true });
      }
      return existing;
    }
    const element = document.createElement('script');
    Object.keys(attributes).forEach((name) => element.setAttribute(name, attributes[name]));
    element.setAttribute('data-brxl-allow', 'true');
    element.src = url;
    element.addEventListener('load', () => {
      element.dataset.brxlLoaded = 'true';
      if (typeof onload === 'function') onload();
    }, { once: true });
    (document.head || document.documentElement).appendChild(element);
    return element;
  }

  function loadOneTrust() {
    if (runtime.oneTrustLoaded || !config.oneTrustDomainId) return;
    runtime.oneTrustLoaded = true;
    if (document.querySelector('script[src*="cdn.cookielaw.org/scripttemplates/otSDKStub.js"]')) return;
    const element = document.createElement('script');
    element.async = true;
    element.type = 'text/javascript';
    element.charset = 'UTF-8';
    element.setAttribute('data-domain-script', config.oneTrustDomainId);
    element.setAttribute('data-brxl-allow', 'true');
    element.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    (document.head || document.documentElement).appendChild(element);
  }

  function loadGtm() {
    if (runtime.gtmLoaded || !config.gtmId || !isVendorAllowed('gtm')) return;
    runtime.gtmLoaded = true;
    window.dataLayer = window.dataLayer || [];
    ensureDataLayerGuard();
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js', brxl_consent_gate: true });
    const element = document.createElement('script');
    element.async = true;
    element.setAttribute('data-brxl-allow', 'true');
    element.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(config.gtmId);
    (document.head || document.documentElement).appendChild(element);
  }

  function loadGoogleAds() {
    if (runtime.googleAdsLoaded || !config.googleAdsId || !isVendorAllowed('google_ads')) return;
    runtime.googleAdsLoaded = true;
    const element = document.createElement('script');
    element.async = true;
    element.setAttribute('data-brxl-allow', 'true');
    element.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.googleAdsId);
    (document.head || document.documentElement).appendChild(element);
    window.gtag('js', new Date());
    window.gtag('config', config.googleAdsId, {
      page_location: sanitizeMeasurementUrl(window.location.href, canPreserveAttribution(getEffectiveState()))
    });
  }

  function hasFireworkComponent() {
    return Boolean(document.querySelector('fw-storyblock, fw-embed-feed, fw-carousel, fw-grid, fw-player'));
  }

  function isFireworkTrackingAllowed(effective = getEffectiveState()) {
    return isVendorAllowed('firework_tracking', effective);
  }

  function getFireworkCookieConsent(effective = getEffectiveState()) {
    if (!isFireworkTrackingAllowed(effective)) return null;
    const categories = ['essential'];
    if (effective.preferencesAllowed) categories.push('functional');
    if (effective.analyticsAllowed) categories.push('performance');
    if (effective.marketingAllowed) categories.push('targeting');
    return categories.join(', ');
  }

  function syncFireworkConsent(effective = getEffectiveState()) {
    const firework = window._fwn;
    if (!firework) return false;
    const trackingAllowed = isFireworkTrackingAllowed(effective);
    const cookieConsent = getFireworkCookieConsent(effective);
    const analyticsConsent = trackingAllowed ? 'non-essential' : 'essential';
    let cookiesSynced = false;
    let analyticsSynced = false;

    if (firework.cookies && typeof firework.cookies.setConsent === 'function') {
      cookiesSynced = true;
      if (runtime.lastFireworkCookieConsent !== cookieConsent) {
        try {
          if (cookieConsent === null && typeof firework.cookies.disableCookies === 'function') {
            firework.cookies.disableCookies('');
          } else {
            firework.cookies.setConsent(cookieConsent);
          }
          runtime.lastFireworkCookieConsent = cookieConsent;
        } catch (error) {
          cookiesSynced = false;
        }
      }
    }

    if (firework.analytics && typeof firework.analytics.setConsent === 'function') {
      analyticsSynced = true;
      if (runtime.lastFireworkAnalyticsConsent !== analyticsConsent) {
        try {
          firework.analytics.setConsent(analyticsConsent);
          runtime.lastFireworkAnalyticsConsent = analyticsConsent;
        } catch (error) {
          analyticsSynced = false;
        }
      }
    }

    return cookiesSynced && analyticsSynced;
  }

  function scheduleFireworkConsentSync(delay = 0, effective = getEffectiveState()) {
    const signature = JSON.stringify({
      trackingAllowed: isFireworkTrackingAllowed(effective),
      analyticsAllowed: effective.analyticsAllowed,
      marketingAllowed: effective.marketingAllowed,
      preferencesAllowed: effective.preferencesAllowed
    });
    if (runtime.fireworkConsentSignature !== signature) {
      runtime.fireworkConsentSignature = signature;
      runtime.fireworkConsentSyncAttempts = 0;
    }
    if (runtime.fireworkConsentSyncTimer) return;
    runtime.fireworkConsentSyncTimer = window.setTimeout(() => {
      runtime.fireworkConsentSyncTimer = null;
      runtime.fireworkConsentSyncAttempts += 1;
      const synced = syncFireworkConsent(getEffectiveState());
      if (!synced && runtime.fireworkConsentSyncAttempts < 40) scheduleFireworkConsentSync(500);
    }, delay);
  }

  function loadFireworkIfNeeded() {
    if (!config.fireworkBusinessId || !hasFireworkComponent()) return false;
    if (runtime.fireworkLoaded || window._fwn) {
      runtime.fireworkLoaded = true;
      scheduleFireworkConsentSync();
      return true;
    }
    if (runtime.fireworkLoadAttempted) return false;
    runtime.fireworkLoadAttempted = true;
    loadScriptOnce('https://asset.fwcdn3.com/js/module/integrations/shopify.js', { async: '', type: 'module' });
    loadScriptOnce('https://asset.fwcdn3.com/js/module/fwn.js?business_id=' + encodeURIComponent(config.fireworkBusinessId), {
      async: '',
      type: 'module',
      'data-fwparam_cookies_opt_in': 'true',
      'data-fwparam_cookies_root': 'false',
      'data-fwparam_tracking_opt_in': 'true'
    }, () => {
      runtime.fireworkLoaded = true;
      scheduleFireworkConsentSync();
    });
    return true;
  }

  function loadKlaviyoTrackingIfAllowed() {
    if (!isVendorAllowed('klaviyo_tracking')) {
      setKlaviyoTrackingSwitch(false);
      return;
    }
    setKlaviyoTrackingSwitch(true);
    // The Klaviyo app embed owns the script URL.  We release a held script
    // after consent and let the app supply its normal company configuration.
    runtime.klaviyoLoaded = true;
  }

  function scheduleTrackerRelease() {
    const run = () => {
      const effective = getEffectiveState();
      if (isVendorAllowed('gtm', effective)) loadGtm();
      if (isVendorAllowed('google_ads', effective)) loadGoogleAds();
      loadFireworkIfNeeded();
      scheduleFireworkConsentSync(0, effective);
      loadKlaviyoTrackingIfAllowed();
      releaseBlockedResources();
      publishState(effective);
      dispatchSearchEventWhenReady();
    };
    if (runtime.domReady) {
      run();
      return;
    }
    if (runtime.trackerScheduleStarted) return;
    runtime.trackerScheduleStarted = true;
    document.addEventListener('DOMContentLoaded', run, { once: true });
  }

  function updateGoogleConsent(effective) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      ad_storage: effective.marketingAllowed ? 'granted' : 'denied',
      analytics_storage: effective.analyticsAllowed ? 'granted' : 'denied',
      ad_user_data: effective.marketingAllowed ? 'granted' : 'denied',
      ad_personalization: effective.marketingAllowed ? 'granted' : 'denied'
    });
  }

  function getShopifyCustomerPrivacy() {
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') return null;
    return customerPrivacy;
  }

  function loadShopifyCustomerPrivacy() {
    const existing = getShopifyCustomerPrivacy();
    if (existing) return Promise.resolve(existing);
    if (runtime.customerPrivacyLoadPromise) return runtime.customerPrivacyLoadPromise;

    runtime.customerPrivacyLoadPromise = new Promise((resolve) => {
      let settled = false;

      const finish = (customerPrivacy) => {
        if (settled) return;
        settled = true;
        runtime.customerPrivacyLoadPromise = null;
        resolve(customerPrivacy || null);
      };

      const tryLoad = () => {
        if (settled) return;
        const customerPrivacy = getShopifyCustomerPrivacy();
        if (customerPrivacy) {
          finish(customerPrivacy);
          return;
        }

        runtime.customerPrivacyLoadAttempts += 1;
        if (runtime.customerPrivacyLoadAttempts > 120) {
          finish(null);
          return;
        }

        const shopify = window.Shopify;
        if (!shopify || typeof shopify.loadFeatures !== 'function') {
          window.setTimeout(tryLoad, 100);
          return;
        }

        let callbackReceived = false;
        try {
          shopify.loadFeatures([
            { name: 'consent-tracking-api', version: '0.1' }
          ], (error) => {
            callbackReceived = true;
            const loadedCustomerPrivacy = getShopifyCustomerPrivacy();
            if (!error && loadedCustomerPrivacy) {
              finish(loadedCustomerPrivacy);
              return;
            }
            window.setTimeout(tryLoad, 100);
          });
        } catch (error) {
          window.setTimeout(tryLoad, 100);
          return;
        }

        window.setTimeout(() => {
          if (!callbackReceived) tryLoad();
        }, 500);
      };

      tryLoad();
    });

    return runtime.customerPrivacyLoadPromise;
  }

  function flushShopifyConsent() {
    if (runtime.shopifyConsentSyncing || !runtime.pendingShopifyConsent) return;
    loadShopifyCustomerPrivacy().then((customerPrivacy) => {
      if (!customerPrivacy || runtime.shopifyConsentSyncing || !runtime.pendingShopifyConsent) return;

      const desired = runtime.pendingShopifyConsent;
      const signature = JSON.stringify(desired);
      runtime.pendingShopifyConsent = null;
      runtime.shopifyConsentSyncing = true;

      const complete = (synced) => {
        runtime.shopifyConsentSyncing = false;
        if (synced) runtime.lastShopifyConsent = signature;
        else {
          runtime.lastShopifyConsent = '';
          runtime.pendingShopifyConsent = desired;
        }
        document.dispatchEvent(new CustomEvent('brxl:shopify-consent-synced', {
          detail: { synced, consent: desired }
        }));
        bindCustomerPrivacy();
        resolveRegion();
        if (runtime.pendingShopifyConsent) window.setTimeout(flushShopifyConsent, 250);
        else applyPrivacyState();
      };

      try {
        customerPrivacy.setTrackingConsent(desired, () => complete(true));
      } catch (error) {
        complete(false);
      }
    });
  }

  function syncShopifyConsent(effective) {
    // Do not manufacture a Shopify consent decision on first visit. Native
    // first-party reporting is controlled by its own source/endpoint gate;
    // Shopify Customer Events and App Pixels receive consent only after a
    // real visitor choice (or an explicit GPC/manual opt-out).
    if (effective.explicitChoice === 'none' && !effective.gpc) return false;
    const desired = {
      analytics: Boolean(effective.analyticsAllowed),
      marketing: Boolean(effective.marketingAllowed),
      preferences: Boolean(effective.preferencesAllowed),
      sale_of_data: Boolean(effective.saleOfDataAllowed)
    };
    const signature = JSON.stringify(desired);
    if (signature === runtime.lastShopifyConsent && !runtime.pendingShopifyConsent) return true;
    runtime.pendingShopifyConsent = desired;
    flushShopifyConsent();
    return false;
  }

  function publishState(effective) {
    const signature = JSON.stringify({
      region: effective.region,
      regionReady: effective.regionReady,
      gpc: effective.gpc,
      explicitChoice: effective.explicitChoice,
      analyticsAllowed: effective.analyticsAllowed,
      marketingAllowed: effective.marketingAllowed,
      preferencesAllowed: effective.preferencesAllowed,
      saleOfDataAllowed: effective.saleOfDataAllowed,
      thirdPartyTrackingAllowed: effective.thirdPartyTrackingAllowed,
      shopifyFirstPartyAnalyticsAllowed: effective.shopifyFirstPartyAnalyticsAllowed,
      shopifyCustomerEventsAllowed: effective.shopifyCustomerEventsAllowed
    });
    state.region = effective.region;
    state.regionReady = effective.regionReady;
    state.gpc = effective.gpc;
    state.explicitChoice = effective.explicitChoice;
    state.analyticsAllowed = effective.analyticsAllowed;
    state.marketingAllowed = effective.marketingAllowed;
    state.preferencesAllowed = effective.preferencesAllowed;
    state.saleOfDataAllowed = effective.saleOfDataAllowed;
    state.thirdPartyTrackingAllowed = effective.thirdPartyTrackingAllowed;
    state.shopifyFirstPartyAnalyticsAllowed = effective.shopifyFirstPartyAnalyticsAllowed;
    state.shopifyCustomerEventsAllowed = effective.shopifyCustomerEventsAllowed;
    state.shopifyAnalyticsAllowed = effective.shopifyAnalyticsAllowed;
    state.source = effective.source;
    state.updatedAt = Date.now();
    window.BRXLPrivacy = window.BRXLPrivacy || {};
    window.BRXLPrivacy.state = { ...state };
    window.BRXLPrivacy.getState = getEffectiveState;
    window.BRXLPrivacy.isVendorAllowed = (vendor) => isVendorAllowed(vendor, getEffectiveState());
    window.BRXLPrivacy.getSafePageLocation = getSafePageLocation;
    window.BRXLPrivacy.trackSearch = trackSearch;
    window.BRXLPrivacy.optOut = handleManualOptOut;
    document.documentElement.classList.toggle('privacy-region-us-ca', effective.region === 'USCA');
    document.documentElement.classList.toggle('privacy-region-unknown', effective.region === 'UNKNOWN');
    document.documentElement.classList.toggle('privacy-opted-out', effective.gpc || effective.explicitChoice === 'reject');
    document.documentElement.classList.toggle('privacy-choice-pending', effective.region === 'USCA' && effective.explicitChoice === 'none');

    try {
      window.sessionStorage.setItem('brxl_privacy_context', JSON.stringify({
        region: effective.region,
        regionReady: effective.regionReady,
        explicitChoice: effective.explicitChoice,
        thirdPartyTrackingAllowed: effective.thirdPartyTrackingAllowed,
        shopifyFirstPartyAnalyticsAllowed: effective.shopifyFirstPartyAnalyticsAllowed,
        shopifyCustomerEventsAllowed: effective.shopifyCustomerEventsAllowed,
        shopifyAnalyticsAllowed: effective.shopifyAnalyticsAllowed,
        pageLocation: getSafePageLocation(),
        updatedAt: Date.now()
      }));
    } catch (error) {
      // Storage is optional and may be blocked by the browser.
    }

    if (signature === runtime.lastPublished) return;
    runtime.lastPublished = signature;
    ensureDataLayerGuard();
    window.dataLayer.push({
      event: 'brxl_privacy_state',
      brxl_privacy: { ...state },
      page_location_redacted: getSafePageLocation()
    });
    document.dispatchEvent(new CustomEvent('brxl:privacy-state-changed', { detail: { ...state } }));
  }

  function clearAndSync(effective) {
    if (effective.gpc || effective.explicitChoice === 'reject' || (effective.region === 'USCA' && effective.explicitChoice === 'none')) {
      clearNonessentialIdentifiers();
      if (!runtime.nonessentialCleanupTimer) {
        let attempts = 0;
        runtime.nonessentialCleanupTimer = window.setInterval(() => {
          const current = getEffectiveState();
          if (!(current.gpc || current.explicitChoice === 'reject' || (current.region === 'USCA' && current.explicitChoice === 'none')) || attempts >= 30) {
            window.clearInterval(runtime.nonessentialCleanupTimer);
            runtime.nonessentialCleanupTimer = null;
            return;
          }
          attempts += 1;
          clearNonessentialIdentifiers();
        }, 500);
      }
    } else if (runtime.nonessentialCleanupTimer) {
      window.clearInterval(runtime.nonessentialCleanupTimer);
      runtime.nonessentialCleanupTimer = null;
    }
    setKlaviyoTrackingSwitch(isVendorAllowed('klaviyo_tracking', effective));
    if (hasFireworkComponent() || runtime.fireworkLoaded || window._fwn) {
      scheduleFireworkConsentSync(0, effective);
    }
  }

  function applyPrivacyState() {
    const inferredChoice = inferChoiceFromOneTrust();
    if (inferredChoice !== 'none') state.explicitChoice = inferredChoice;
    const effective = getEffectiveState();
    const previous = runtime.lastApplied;
    runtime.lastApplied = JSON.stringify(effective);
    publishState(effective);
    updateGoogleConsent(effective);
    syncShopifyConsent(effective);
    clearAndSync(effective);
    if (effective.regionReady) {
      scheduleTrackerRelease();
      releaseBlockedResources();
      if (effective.region === 'OTHER' || effective.explicitChoice !== 'none') dispatchSearchEventWhenReady();
    }
    if (previous && previous.indexOf('"thirdPartyTrackingAllowed":true') !== -1 && !effective.thirdPartyTrackingAllowed) {
      scheduleRevocationReload();
    }
    return effective;
  }

  function scheduleRevocationReload() {
    if (runtime.revocationReloadScheduled) return;
    if (!(runtime.gtmLoaded || runtime.googleAdsLoaded || runtime.klaviyoLoaded || runtime.fireworkLoaded)) return;
    runtime.revocationReloadScheduled = true;
    window.setTimeout(() => {
      if (getEffectiveState().thirdPartyTrackingAllowed) {
        runtime.revocationReloadScheduled = false;
        return;
      }
      window.location.reload();
    }, 250);
  }

  function trackSearch(term, resultCount) {
    const effective = getEffectiveState();
    if (effective.region !== 'OTHER' || !effective.regionReady || !effective.analyticsAllowed || effective.gpc) return false;
    const value = String(term || '').trim().slice(0, 200);
    if (!value) return false;
    ensureDataLayerGuard();
    window.dataLayer.push({
      event: 'search_performed',
      search_term: value,
      search_results_count: Number.isFinite(Number(resultCount)) ? Number(resultCount) : undefined,
      page_location: getSafePageLocation(),
      page_referrer: getSafeReferrer(),
      brxl_search_destination: 'approved-analytics-owner'
    });
    runtime.searchEventSent = true;
    return true;
  }

  function dispatchSearchEventWhenReady() {
    if (runtime.searchEventSent || config.pageType !== 'search') return;
    const element = document.querySelector('[data-brxl-search-page]');
    if (!element) return;
    const term = element.getAttribute('data-brxl-search-term') || '';
    const count = element.getAttribute('data-brxl-search-results') || '';
    trackSearch(term, count);
  }

  function bindOneTrust() {
    if (runtime.oneTrustBound) return;
    const oneTrust = getOneTrust();
    if (!oneTrust) return;
    runtime.oneTrustBound = true;
    try {
      if (typeof oneTrust.OnConsentChanged === 'function') {
        oneTrust.OnConsentChanged(() => {
          runtime.manualOptOut = false;
          safeStorageRemove(window.localStorage, KEYS.optOut);
          state.explicitChoice = 'none';
          const choice = inferChoiceFromOneTrust();
          if (choice !== 'none') {
            state.explicitChoice = choice;
            writeStoredChoice(choice);
          }
          applyPrivacyState();
          window.setTimeout(applyPrivacyState, 250);
        });
      }
    } catch (error) {
      // OneTrust versions differ; OptanonWrapper remains the fallback.
    }
  }

  function openPreferencesLoop(attempt = 0) {
    if (!runtime.preferenceRequested) {
      runtime.preferenceLoop = false;
      return;
    }
    const oneTrust = getOneTrust();
    const preferenceCenter = document.getElementById('onetrust-pc-sdk');
    if (preferenceCenter && window.getComputedStyle(preferenceCenter).display !== 'none') {
      runtime.preferenceRequested = false;
      runtime.preferenceLoop = false;
      return;
    }
    if (runtime.oneTrustReadyAt && oneTrust && typeof oneTrust.ToggleInfoDisplay === 'function') {
      runtime.preferenceRequested = false;
      runtime.preferenceLoop = false;
      oneTrust.ToggleInfoDisplay();
      return;
    }
    if (attempt > 60) {
      runtime.preferenceRequested = false;
      runtime.preferenceLoop = false;
      return;
    }
    window.setTimeout(() => openPreferencesLoop(attempt + 1), 100);
  }

  function startPreferencesLoop() {
    if (runtime.preferenceLoop) return;
    runtime.preferenceLoop = true;
    openPreferencesLoop();
  }

  function handleManualOptOut() {
    runtime.manualOptOut = true;
    safeStorageSet(window.localStorage, KEYS.optOut, '1');
    writeStoredChoice('reject');
    state.explicitChoice = 'reject';
    const effective = applyPrivacyState();
    syncShopifyConsent(effective);
    const oneTrust = getOneTrust();
    try {
      if (oneTrust && typeof oneTrust.UpdateConsent === 'function') {
        oneTrust.UpdateConsent('Category', config.categoryAnalytics + ':0');
        oneTrust.UpdateConsent('Category', config.categoryMarketing + ':0');
        oneTrust.UpdateConsent('Category', config.categoryPreferences + ':0');
      }
    } catch (error) {
      // Customer Privacy remains the storefront fallback if an old OneTrust
      // build does not expose UpdateConsent.
    }
    return effective;
  }

  function bindInteractionHandlers() {
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-privacy-opt-out]')) {
        event.preventDefault();
        handleManualOptOut();
        return;
      }
      if (target.closest('[data-onetrust-preferences]')) {
        event.preventDefault();
        runtime.preferenceRequested = true;
        loadOneTrust();
        startPreferencesLoop();
        return;
      }
    }, true);

    document.addEventListener('brxl:one-trust-ready', () => {
      runtime.oneTrustReadyAt = Date.now();
      bindOneTrust();
      resolveRegion();
      applyPrivacyState();
      startPreferencesLoop();
    });

    document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => {
      bindCustomerPrivacy();
      resolveRegion();
      applyPrivacyState();
    });

    document.addEventListener('visitorConsentCollected', () => {
      resolveRegion();
      applyPrivacyState();
    });

    document.addEventListener('DOMContentLoaded', () => {
      runtime.domReady = true;
      dispatchSearchEventWhenReady();
      resolveRegion();
      applyPrivacyState();
    }, { once: true });
  }

  function bindCustomerPrivacy() {
    if (runtime.customerPrivacyBound) return;
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!customerPrivacy) return;
    runtime.customerPrivacyBound = true;
    ['visitorConsentCollected', 'trackingConsentAccepted', 'trackingConsentDenied'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        resolveRegion();
        applyPrivacyState();
      });
    });
  }

  function resolveRegion() {
    if (state.regionReady) {
      bindCustomerPrivacy();
      bindOneTrust();
      return true;
    }
    const shopifyRegion = readShopifyRegion();
    const oneTrustRegion = readOneTrustRegion();
    const signals = [
      { value: shopifyRegion, source: 'shopify-customer-privacy' },
      { value: oneTrustRegion, source: 'onetrust-geolocation' }
    ].filter((signal) => signal.value);
    // A CA signal wins if the two providers disagree. Only a remaining,
    // definitive non-CA signal may promote UNKNOWN to OTHER.
    const caSignal = signals.find((signal) => isCaliforniaRegion(signal.value));
    const resolved = caSignal || signals.find((signal) => isDefinitiveOtherRegion(signal.value));
    if (resolved) {
      const source = caSignal && signals.length > 1 ? 'shopify-and-onetrust-ca' : resolved.source;
      applyRegion(resolved.value, source);
    }
    if (state.regionReady) {
      state.explicitChoice = inferChoiceFromOneTrust();
      bindCustomerPrivacy();
      bindOneTrust();
      applyPrivacyState();
      return true;
    }
    return false;
  }

  function startRegionPolling() {
    if (runtime.regionPoll) return;
    let attempts = 0;
    runtime.regionPoll = window.setInterval(() => {
      attempts += 1;
      resolveRegion();
      if (state.regionReady || attempts >= 120) {
        window.clearInterval(runtime.regionPoll);
        runtime.regionPoll = null;
        // UNKNOWN deliberately remains UNKNOWN. There is no timeout-based
        // promotion to OTHER, so an unavailable signal cannot release tags.
        applyPrivacyState();
      }
    }, 100);
  }

  function bootstrap() {
    window.BRXLPrivacy = window.BRXLPrivacy || {};
    window.BRXLPrivacy.state = { ...state };
    window.BRXLPrivacy.getState = getEffectiveState;
    window.BRXLPrivacy.isVendorAllowed = (vendor) => isVendorAllowed(vendor, getEffectiveState());
    window.BRXLPrivacy.getSafePageLocation = getSafePageLocation;
    window.BRXLPrivacy.trackSearch = trackSearch;
    window.BRXLPrivacy.optOut = handleManualOptOut;
    runtime.manualOptOut = safeStorageGet(window.localStorage, KEYS.optOut) === '1';
    state.explicitChoice = readStoredChoice();
    ensureDataLayerGuard();
    installNetworkGuards();
    bindInteractionHandlers();
    // OneTrust is a consent runtime and is explicitly allowlisted by the
    // network guard. Load it before app embeds are released.
    loadOneTrust();
    loadShopifyCustomerPrivacy().then((customerPrivacy) => {
      if (!customerPrivacy) return;
      bindCustomerPrivacy();
      resolveRegion();
      applyPrivacyState();
      flushShopifyConsent();
    });
    resolveRegion();
    applyPrivacyState();
    startRegionPolling();
    runtime.customerPrivacyPoll = window.setInterval(() => {
      bindCustomerPrivacy();
      resolveRegion();
      if (state.regionReady) {
        window.clearInterval(runtime.customerPrivacyPoll);
        runtime.customerPrivacyPoll = null;
      }
    }, 250);
  }

  bootstrap();
})();
